import {
  Button,
  useDisclosure,
  MenuItem,
  Skeleton,
  Tooltip,
} from '@chakra-ui/react';
import React from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  FundingRequestEntitiesFragmentGQLType,
  ShareFundingComponentFragmentGQLType,
  ResolvedLoanFragmentGQLType,
  PrincipalPaymentMetricsGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { MarkLenderShareComponentFundedModal } from './MarkFundedModal';
import { useCalculateMinFundTransferDate } from './markFundTransferHooks';
import { useGetPrincipalPaymentMetricsByLoanIdQuery } from '~/packages/invoices/requests/invoices.gql.generated';

const calculateIsDisabled = (params: {
  fundingComponent: ShareFundingComponentFragmentGQLType;
  principalPaymentMetrics: PrincipalPaymentMetricsGQLType;
  resolvedLoan: ResolvedLoanFragmentGQLType;
  minDate: Date;
}): { isDisabled: boolean; disabledMessage: string } => {
  const {
    fundingComponent,
    principalPaymentMetrics,
    resolvedLoan,
    minDate,
  } = params;
  const {
    lenderOrganizationId,
    fundingRequestComponentId,
    fundTransferDate,
  } = fundingComponent;

  // *** Admin Agent ***
  // Currently Admin Agent already assumes automatic funding and
  // cannot change the fund transfer date.
  const isAdminAgent =
    resolvedLoan.parties.adminAgentOrganization?.id ===
    fundingComponent.lenderOrganizationId;

  if (isAdminAgent) {
    return {
      isDisabled: true,
      disabledMessage: 'Admin Agent cannot change fund transfer',
    };
  }

  // *** Principal Payment ***
  if (
    fundTransferDate &&
    principalPaymentMetrics.loanHasPayment &&
    fundTransferDate <= commonDate.dateToFullDateString(minDate)
  ) {
    return {
      isDisabled: true,
      disabledMessage:
        'Cannot change fund transfer date after principal payment has been made',
    };
  }

  // *** Funding Request Ordering ***
  // If the lender has an earlier request that hasn't been funded yet,
  // then we want to disable allowing them to mark a later one first.
  // Only care about this if the funding component isn't already marked funded
  if (!fundingComponent.fundTransferDate) {
    const sortedFundingRequests = resolvedLoan.funding.requests
      .slice()
      .sort((a, b) => a.originationDate.localeCompare(b.originationDate));

    let isEarliestLenderNonFundedComponent = false;
    for (const request of sortedFundingRequests) {
      const earliestNonFundedComponent = request.fundingComponents.find(
        (component) =>
          component.lenderOrganizationId === lenderOrganizationId &&
          !component.fundTransferDate,
      );
      if (earliestNonFundedComponent) {
        isEarliestLenderNonFundedComponent =
          earliestNonFundedComponent.fundingRequestComponentId ===
          fundingRequestComponentId;
        break;
      }
    }

    if (!isEarliestLenderNonFundedComponent) {
      return {
        isDisabled: true,
        disabledMessage: 'Mark prior request as funded first for this lender',
      };
    }
  }

  return { isDisabled: false, disabledMessage: '' };
};

interface MarkLenderShareComponentFundedButtonProps {
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
  fundingComponent: ShareFundingComponentFragmentGQLType;
  resolvedLoan: ResolvedLoanFragmentGQLType;
}

export const MarkLenderShareComponentFundedButton = ({
  fundingRequest,
  fundingComponent,
  resolvedLoan,
}: MarkLenderShareComponentFundedButtonProps) => {
  const modalControls = useDisclosure();

  const principalPaymentMetricsQuery = useGetPrincipalPaymentMetricsByLoanIdQuery(
    {
      fetchPolicy: 'cache-and-network',
      variables: { loanId: resolvedLoan.id },
    },
  );
  if (principalPaymentMetricsQuery.error) {
    throw principalPaymentMetricsQuery.error;
  }

  const minDate = useCalculateMinFundTransferDate({
    principalPaymentMetrics: principalPaymentMetricsQuery.data?.result ?? null,
    fundingRequest,
  });

  if (
    principalPaymentMetricsQuery.loading ||
    !principalPaymentMetricsQuery.data
  ) {
    return <Skeleton height="full" width="full" />;
  }

  const { isDisabled, disabledMessage } = calculateIsDisabled({
    fundingComponent,
    principalPaymentMetrics: principalPaymentMetricsQuery.data.result,
    resolvedLoan,
    minDate,
  });

  return (
    <>
      <Tooltip label={disabledMessage} isDisabled={!isDisabled}>
        <Button
          aria-label="mark funded"
          onClick={modalControls.onOpen}
          variant="secondary"
          colorScheme="v2-aurora"
          isDisabled={isDisabled}
        >
          Mark Funded
        </Button>
      </Tooltip>
      <MarkLenderShareComponentFundedModal
        loanId={resolvedLoan.id}
        fundingComponent={fundingComponent}
        modalControls={modalControls}
        minDate={minDate}
      />
    </>
  );
};

export const MarkLenderShareComponentFundedMenuItem = ({
  fundingRequest,
  fundingComponent,
  resolvedLoan,
}: MarkLenderShareComponentFundedButtonProps) => {
  const modalControls = useDisclosure();

  const principalPaymentMetricsQuery = useGetPrincipalPaymentMetricsByLoanIdQuery(
    {
      fetchPolicy: 'cache-and-network',
      variables: { loanId: resolvedLoan.id },
    },
  );
  if (principalPaymentMetricsQuery.error) {
    throw principalPaymentMetricsQuery.error;
  }

  const minDate = useCalculateMinFundTransferDate({
    principalPaymentMetrics: principalPaymentMetricsQuery.data?.result ?? null,
    fundingRequest,
  });

  if (
    principalPaymentMetricsQuery.loading ||
    !principalPaymentMetricsQuery.data
  ) {
    return <Skeleton height="full" width="full" />;
  }

  const isAlreadyMarkFunded = !!fundingComponent.fundTransferDate;

  const buttonText = isAlreadyMarkFunded
    ? 'Change Lender Funded Date'
    : 'Mark Funded';

  const { isDisabled, disabledMessage } = calculateIsDisabled({
    fundingComponent,
    principalPaymentMetrics: principalPaymentMetricsQuery.data.result,
    resolvedLoan,
    minDate,
  });

  return (
    <MenuItem as="div">
      <Tooltip label={disabledMessage} isDisabled={!isDisabled}>
        <Button
          variant="ghost"
          colorScheme="gray"
          fontWeight={400}
          onClick={modalControls.onOpen}
          isDisabled={isDisabled}
        >
          {buttonText}
        </Button>
      </Tooltip>
      <MarkLenderShareComponentFundedModal
        loanId={resolvedLoan.id}
        fundingComponent={fundingComponent}
        modalControls={modalControls}
        minDate={minDate}
      />
    </MenuItem>
  );
};
